import { create } from 'zustand';
import { supabase } from '@/lib/supabase/client';
import { DepartmentMember } from '@/types/departments';

interface AvailableMembersState {
  members: DepartmentMember[];
  isLoading: boolean;
  error: string | null;
  fetchMembers: () => Promise<void>;
  cleanup: () => void;
}

export const useAvailableMembersStore = create<AvailableMembersState>((set, get) => {
  // Create a reference to store the subscription channel
  let subscriptionChannel: ReturnType<typeof supabase.channel> | null = null;

  return {
    members: [],
    isLoading: false,
    error: null,

    fetchMembers: async () => {
      // If already loading, don't fetch again
      if (get().isLoading) return;

      try {
        set({ isLoading: true, error: null });
        
        const { data: workers, error: workersError } = await supabase
          .from('users')
          .select(`
            id,
            full_name,
            email,
            role,
            status,
            department_id
          `)
          .eq('role', 'worker')
          .eq('status', 'active')
          .is('department_id', null)
          .order('full_name');

        if (workersError) throw workersError;

        const transformedMembers = (workers || []).map(worker => {
          const names = worker.full_name?.split(' ') || [''];
          return {
            id: worker.id,
            firstName: names[0],
            lastName: names.slice(1).join(' '),
            email: worker.email,
            role: worker.role,
            departmentRole: 'member'
          };
        });

        set({ 
          members: transformedMembers,
          error: null 
        });
      } catch (err) {
        console.error('Error fetching available members:', err);
        set({ 
          error: err instanceof Error ? err.message : 'Failed to fetch available members',
          members: []
        });
      } finally {
        set({ isLoading: false });
      }
    },

    cleanup: () => {
      // Clean up subscription when store is no longer needed
      if (subscriptionChannel) {
        supabase.removeChannel(subscriptionChannel);
        subscriptionChannel = null;
      }
    }
  };
});

// Initialize subscription only once
const initializeSubscription = () => {
  const channel = supabase
    .channel('available_members_store')
    .on(
      'postgres_changes',
      { 
        event: '*', 
        schema: 'public', 
        table: 'users',
        filter: "role=eq.worker" 
      },
      () => {
        const store = useAvailableMembersStore.getState();
        store.fetchMembers();
      }
    )
    .subscribe();

  return channel;
};

// Set up subscription once
const channel = initializeSubscription();

// Clean up subscription when window unloads
window.addEventListener('unload', () => {
  if (channel) {
    supabase.removeChannel(channel);
  }
});